import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Servicios", href: "#servicios" },
    { name: "Portafolio", href: "#portafolio" },
    { name: "Nosotros", href: "#nosotros" },
    { name: "Proceso", href: "#proceso" },
    { name: "Testimonios", href: "#testimonios" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-[#030303]/80 nav-blur border-b border-white/5"
            : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a
              href="#"
              className="flex items-center gap-2 group"
              data-testid="navbar-logo"
            >
              <img 
                src="/logo-vibrant.png" 
                alt="klyvora7" 
                className="h-24 md:h-36 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-zinc-400 hover:text-white transition-colors duration-200 text-sm font-medium"
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </button>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => scrollToSection("#contacto")}
                className="bg-gradient-to-r from-[#2979FF] to-[#00E5FF] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:shadow-[0_0_25px_rgba(0,229,255,0.4)] hover:-translate-y-0.5 transition-all duration-300"
                data-testid="navbar-cta-button"
              >
                Contactar
              </button>
              <a
                href="/admin/login"
                className="bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300"
                data-testid="navbar-login-button"
              >
                Iniciar Sesión
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-white p-2"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#030303]/95 nav-blur md:hidden pt-24"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center gap-6 p-6">
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => scrollToSection(link.href)}
                  className="text-white text-xl font-medium hover:text-[#00E5FF] transition-colors"
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={() => scrollToSection("#contacto")}
                className="mt-4 bg-gradient-to-r from-[#2979FF] to-[#00E5FF] text-white px-8 py-3 rounded-full text-lg font-medium"
                data-testid="mobile-cta-button"
              >
                Contactar
              </motion.button>
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                href="/admin/login"
                className="mt-2 bg-white/5 border border-white/10 text-white px-8 py-3 rounded-full text-lg font-medium"
                data-testid="mobile-login-button"
              >
                Iniciar Sesión
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
